import type { TFunction } from "react-i18next";
import type { SignatureDocument } from "../@types/signatures";
import { resendWhatsapp, sendSigningSet } from "./signatures";
import { deliveryRecipientLabel } from "../utils/signatureWhatsappShare";

/**
 * The automatic send ("שליחה אוטומטית", WhatsApp AND SMS) from any surface — the sent list's row, the
 * detail panel, the client's signatures tab.
 *
 * A contract that belongs to a SET (one bundle preparation: a seller's brokerage order + exclusivity)
 * sends its whole set as ONE message, with the link of the first contract the client still has to
 * sign; after the SMS code the sign page lists every contract still waiting. One set is one unit
 * everywhere (Trello #231 item 6; Haim, 2026-10-21), so a resend from one row is never a message about
 * that contract alone: every contract the message covers is stamped with it and reads "נשלח", and once
 * only one contract is left, the message is exactly that contract's own. A contract outside any set is
 * sent alone, as always (`resendWhatsapp`).
 */
export async function sendSigningLinkFor(
  doc: Pick<SignatureDocument, "id" | "bundleId">,
  signerId: string
): Promise<{ documents: SignatureDocument[]; covered: number }> {
  if (doc.bundleId) {
    const sent = await sendSigningSet(doc.id, signerId);
    return { documents: sent.documents, covered: sent.coveredDocumentIds.length };
  }
  return { documents: [await resendWhatsapp(doc.id, signerId)], covered: 1 };
}

/** The confirmation after a send: to which number, from Webot's number — and, for a set, that one link covered every contract. */
export function sentConfirmation(t: TFunction, signer: { name?: string; phone?: string }, covered: number): string {
  const to = deliveryRecipientLabel(signer);
  return (covered > 1 ? t("signatures.delivery.sentSetTo", { to, count: covered }) : t("signatures.delivery.sentTo", { to })) as string;
}
